"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/client/api/axios";

export type CategoryStatus = "NEW" | "ACTIVE" | "INACTIVE" | "DELETED";
export type CategoryJobType = "PROPOSAL" | "SITE_VISIT";
export type CategoryBookingType = "BOOK_NOW" | "SCHEDULE" | "REPEAT";

export interface Category {
  id: number;
  cityId: number;
  businessGroupId: number;
  serviceTypeId: number | null;
  serviceType: string | null;
  metadataId: number | null;
  name: string;
  nameSpanish: string | null;
  description: string | null;
  descriptionSpanish: string | null;
  jobTypes: CategoryJobType[];
  bookingTypes: CategoryBookingType[];
  billingStrategy: string | null;
  cancellationFee: number | null;
  ratingParameterIds: number[];
  questionIds: number[];
  bannerImageApp: string | null;
  bannerImageWeb: string | null;
  selectedIconImage: string | null;
  unselectedIconImage: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  seoKeyword: string | null;
  seoImages: string[];
  status: CategoryStatus;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  city?: { id: number; name: string; state: string; country: string };
  businessGroup?: { id: number; name: string };
}

export type CategoryCreatePayload = {
  cityId: number;
  businessGroupId: number;
  name: string;
  bookingTypes: CategoryBookingType[];
  serviceTypeId?: number | null;
  serviceType?: string | null;
  metadataId?: number | null;
  nameSpanish?: string | null;
  description?: string | null;
  descriptionSpanish?: string | null;
  jobTypes?: CategoryJobType[];
  billingStrategy?: string | null;
  cancellationFee?: number | null;
  ratingParameterIds?: number[];
  questionIds?: number[];
  bannerImageApp?: string | null;
  bannerImageWeb?: string | null;
  selectedIconImage?: string | null;
  unselectedIconImage?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeyword?: string | null;
  seoImages?: string[];
};

const CATEGORIES_QUERY_KEY = ["categories"] as const;

export type CategoriesQueryParams = {
  status?: CategoryStatus;
  cityId?: number;
  businessGroupId?: number;
  hasCityServices?: boolean;
};

async function fetchCategories(params?: CategoriesQueryParams) {
  const { hasCityServices, ...rest } = params ?? {};
  const requestParams = params
    ? { ...rest, ...(hasCityServices ? { hasCityServices: "true" as const } : {}) }
    : undefined;
  const { data } = await api.get<{ success: boolean; data: Category[] }>("/categories", {
    params: requestParams,
  });
  return data.data ?? data;
}

async function fetchCategory(id: number) {
  const { data } = await api.get<{ success: boolean; data: Category }>(`/categories/${id}`);
  return data.data ?? data;
}

async function addCategory(payload: CategoryCreatePayload) {
  const { data } = await api.post<{ success: boolean; data: Category }>("/categories", payload);
  return data.data ?? data;
}

async function updateCategory(
  id: number,
  payload: Partial<CategoryCreatePayload> & { status?: CategoryStatus },
) {
  const { data } = await api.put<{ success: boolean; data: Category }>(`/categories/${id}`, payload);
  return data.data ?? data;
}

async function deleteCategory(id: number) {
  await api.delete(`/categories/${id}`);
}

async function bulkDeleteCategories(ids: number[]) {
  await api.post("/categories/bulk-delete", { ids });
}

async function updateCategoryStatus(id: number, status: CategoryStatus) {
  const { data } = await api.patch<{ success: boolean; data: Category }>(`/categories/${id}`, {
    status,
  });
  return data.data ?? data;
}

async function uploadCategoryImage(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  
  const { data } = await api.post<{ success: boolean; url: string }>(
    "/categories/upload-image",
    formData,
  );
  return data;
}

export function useCategoriesQuery(
  params?: CategoriesQueryParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [...CATEGORIES_QUERY_KEY, params ?? "all"],
    queryFn: () => fetchCategories(params),
    enabled: options?.enabled ?? true,
  });
}

export function useCategoryQuery(id: number | null) {
  return useQuery({
    queryKey: [...CATEGORIES_QUERY_KEY, id],
    queryFn: () => fetchCategory(id!),
    enabled: id != null && id > 0,
  });
}

export function useAddCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
  });
}

export function useUpdateCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: Parameters<typeof updateCategory>[1];
    }) => updateCategory(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...CATEGORIES_QUERY_KEY, data.id] });
    },
  });
}

export function useDeleteCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
  });
}

export function useBulkDeleteCategoriesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteCategories,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
  });
}

export function useUpdateCategoryStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: CategoryStatus }) =>
      updateCategoryStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
    },
  });
}

export function useUploadCategoryImageMutation() {
  return useMutation({
    mutationFn: uploadCategoryImage,
  });
}

const CATEGORY_FIELD_LABELS: Record<string, string> = {
  cityId: "City",
  businessGroupId: "Business group",
  name: "Name",
  nameSpanish: "Name (Spanish)",
  description: "Description",
  descriptionSpanish: "Description (Spanish)",
  serviceType: "Service type",
  serviceTypeId: "Service type (ID)",
  metadataId: "Metadata",
  bookingTypes: "Booking types",
  jobTypes: "Job types",
  billingStrategy: "Billing strategy",
  cancellationFee: "Cancellation fee",
  ratingParameterIds: "Rating parameters",
  questionIds: "Questions",
  bannerImageApp: "Banner image (app)",
  bannerImageWeb: "Banner image (web)",
  selectedIconImage: "Selected icon",
  unselectedIconImage: "Unselected icon",
  seoTitle: "SEO title",
  seoDescription: "SEO description",
  seoKeyword: "SEO keyword",
  seoImages: "SEO images",
  status: "Status",
};

export function getCategoryError(error: unknown): string | undefined {
  const err = error as {
    response?: {
      data?: {
        error?: string;
        code?: string;
        hint?: string;
        details?: Array<{ path?: (string | number)[]; message?: string }>;
      };
      status?: number;
    };
  };
  const data = err?.response?.data;
  if (
    data?.code === "VALIDATION_ERROR" &&
    Array.isArray(data?.details) &&
    data.details.length > 0
  ) {
    const messages = data.details
      .map((e: { path?: (string | number)[]; message?: string }) => {
        const field = e.path?.[0];
        const label = typeof field === "string" ? (CATEGORY_FIELD_LABELS[field] ?? field) : "Field";
        return `${label}: ${e.message ?? "Invalid"}`;
      })
      .join(". ");
    return messages || "Validation failed";
  }
  if (data?.error) return data.error;
  if (data?.code === "DATABASE_NOT_READY")
    return data.hint ?? "Database is being updated. Please try again later.";
  if (err?.response?.status === 401) return "Please sign in to continue.";
  return undefined;
}